import type { View, SVGProps } from "@react-pdf/renderer";
import { formatNumber } from "@repo/shared/formatNumber";
import { mapObject } from "@repo/lib/mapObjects";
import type { NumberFormat } from "@repo/types";

const removeUndefined = <T extends object>(obj: T): T => {
  const newObj = {} as T;
  for (const key of Object.keys(obj) as (keyof T)[]) {
    if (obj[key] !== undefined) {
      newObj[key] = obj[key];
    }
  }
  return newObj;
};

// #region Layouts

export type LayoutType =
  | "sidebar"
  | "topAcross"
  | "topCenter"
  | "topAcrossBoard";

export const layoutTable: Record<LayoutType, true> = {
  sidebar: true,
  topAcross: true,
  topCenter: true,
  topAcrossBoard: true,
};
Object.setPrototypeOf(layoutTable, null);

export const coerceLayout = (layout?: string): LayoutType => {
  if (layout && layout in layoutTable) return layout as LayoutType;
  return "sidebar";
};

// #endregion

// #region Themes

export interface TextStyle {
  lineHeight?: number;
  marginTop?: number;
  marginBottom?: number;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  indent?: number;
  color?: string;
}

export interface IconStyle {
  color?: string;
  size?: number;
}

export interface AgendaTheme {
  id: string;
  name: string;
  fontSize: number;
  color?: string;
  lineHeight?: number;
  marginTop?: number;
  marginBottom?: number;
  sectionTitle?: TextStyle;
  sectionDescription?: TextStyle;
  itemTitle?: TextStyle;
  itemDescription?: TextStyle;
  itemAugmented?: TextStyle;
  documentTitle?: TextStyle;
  documentIcon?: boolean | IconStyle;
}

export const themeList: AgendaTheme[] = [
  {
    id: "originalStyle",
    name: "Original Style",
    fontSize: 12,
    marginBottom: 6,
    sectionTitle: {
      bold: true,
      indent: 0,
      marginTop: 12,
      marginBottom: 12,
    },
    sectionDescription: { indent: 18 },
    itemTitle: { indent: 18 },
    itemDescription: { indent: 36 },
    itemAugmented: { indent: 36 },
    documentTitle: {
      indent: 36,
      marginBottom: 4,
    },
    documentIcon: true,
  },

  {
    id: "minimalistic",
    name: "Minimalistic",
    fontSize: 12,
    marginTop: 3,
    marginBottom: 5,
    sectionTitle: {
      bold: true,
      marginTop: 12,
      fontSize: 14,
    },
    documentIcon: {
      size: 12,
      color: "#808080",
    },
  },

  {
    id: "boldSectionsItems",
    name: "Bold Section & Item Titles",
    fontSize: 12,
    marginTop: 3,
    marginBottom: 5,
    sectionTitle: {
      bold: true,
      underline: true,
    },
    itemTitle: {
      bold: true,
    },
    documentIcon: true,
  },

  {
    id: "indented",
    name: "Indented Items & Documents",
    fontSize: 12,
    marginTop: 3,
    marginBottom: 5,
    sectionTitle: {
      bold: true,
      fontSize: 14,
    },
    sectionDescription: { indent: 18 },
    itemTitle: { indent: 18 },
    itemDescription: { indent: 36 },
    itemAugmented: { indent: 36 },
    documentTitle: { indent: 36 },
    documentIcon: true,
  },
];

export const themeTable: Record<string, AgendaTheme> = mapObject(
  themeList,
  (theme) => theme.id
);

export const coerceTheme = (themeId?: string): AgendaTheme => {
  if (themeId && themeId in themeTable) return themeTable[themeId];
  return themeList[0];
};

export type StyleProperties = Exclude<
  React.ComponentProps<typeof View>["style"],
  undefined | unknown[]
>;

/** A table mapping number format options to labels. */
export const numberFormatMap: Record<NumberFormat, string> = {
  none: "None",
  decimal: "Decimal",
  alpha: "Alphabetic",
  lowerAlpha: "Lowercase Alphabetic",
  roman: "Roman Numerals",
  lowerRoman: "Lowercase Roman Numerals",
  bullet: "Bullet",
  bulletOpen: "White Bullet",
  squareBullet: "Square",
  squareBulletOpen: "White Square",
};

export const numberFormatList = (
  Object.keys(numberFormatMap) as NumberFormat[]
).map((key) => ({ label: numberFormatMap[key], value: key }));

export const isBullet = (type: NumberFormat) => {
  switch (type) {
    case "bullet":
    case "squareBullet":
    case "bulletOpen":
    case "squareBulletOpen":
      return true;
  }
  return false;
};

/** Format a single index with the given formatting type. */
export const formatIndex = (index: number, type: NumberFormat) => {
  switch (type) {
    case "bullet":
      return "\u2022";
    case "squareBullet":
      return "\u25aa";
    case "bulletOpen":
      return "\u25e6";
    case "squareBulletOpen":
      return "\u25a1";
    case "decimal":
    case "alpha":
    case "roman":
    case "lowerAlpha":
    case "lowerRoman":
      if (index < 0) return "";
      return formatNumber(index + 1, type);
  }
  return "";
};

/** Format a group of indices, optionally applying nesting. */
export const formatLineIndex = ({
  index = [],
  type = [],
  nested = false,
}: {
  index?: number[];
  type?: NumberFormat[];
  nested?: boolean;
}) => {
  const lastI = index.length - 1;
  const lastIndex = index[lastI];
  const lastType = type[lastI];
  if (lastIndex == null || lastIndex < 0 || !lastType || lastType === "none") {
    return "";
  }

  if (isBullet(lastType)) {
    return formatIndex(lastIndex, lastType) + "  ";
  }

  if (nested) {
    let str: string = "";
    for (let i = 0; i < index.length; i++) {
      if (!type[i] || index[i] == null || index[i] < 0 || isBullet(type[i])) {
        continue;
      }

      if (str) str += ".";
      str += formatIndex(index[i], type[i]);
    }
    return str + "  ";
  }
  return formatIndex(lastIndex, lastType) + ". ";
};

/** Get the React PDF styles to apply, based on the theme and text style. */
export const getStyles = (
  theme?: AgendaTheme,
  textStyle?: TextStyle
): StyleProperties => {
  return removeUndefined({
    marginTop: textStyle?.marginTop ?? theme?.marginTop,
    marginBottom: textStyle?.marginBottom ?? theme?.marginBottom,
    color: textStyle?.color ?? theme?.color,
    fontWeight: textStyle?.bold ? "bold" : "normal",
    fontStyle: textStyle?.italic ? "italic" : "normal",
    textDecoration: textStyle?.underline ? "underline" : "none",
    marginLeft: textStyle?.indent,
    lineHeight: textStyle?.lineHeight ?? theme?.lineHeight,
    fontSize: textStyle?.fontSize ?? theme?.fontSize,
  });
};

/** Get the React PDF icon styles to apply, based on the theme's icon style. */
export const getIconStyles = (iconStyle?: boolean | IconStyle): SVGProps => {
  if (!iconStyle || typeof iconStyle === "boolean") return {};

  return removeUndefined({
    color: iconStyle.color,
    width: iconStyle.size,
    height: iconStyle.size,
  });
};

// #endregion
